import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Store, Users, Video, MessageSquare, Phone, Package, Building2, Mail, LogOut, Send } from 'lucide-react';
import { toast } from 'sonner';
import VideoCall from '../components/VideoCall';
import MessagingPanel from '../components/MessagingPanel';
import { useCurrency } from '../contexts/CurrencyContext';
import AutoTranslatedText from '../components/AutoTranslatedText';

const RepDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [booth, setBooth] = useState(null);
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [activeVideoRoom, setActiveVideoRoom] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    // Check if user is logged in via main auth (new system)
    if (user && user.role === 'rep') {
      fetchRepData();
    } else {
      // Fall back to legacy rep_token system
      const token = localStorage.getItem('rep_token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchRepData();
      } else if (!user) {
        // No auth at all, redirect to login
        navigate('/auth');
      }
    }
  }, [user, navigate]);

  // Polling for real-time notifications - check for new interactions every 15 seconds
  useEffect(() => {
    if (!user || user.role !== 'rep' || !user.booth_id) return;
    
    const pollForUpdates = async () => {
      try {
        const [interactionsRes, unreadRes] = await Promise.all([
          axios.get(`${API}/interactions/booth/${user.booth_id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { unread_count: 0 } }))
        ]);
        
        const newInteractions = interactionsRes.data;
        
        // Check if there are new pending interactions
        const oldPendingCount = interactions.filter(i => i.status === 'pending').length;
        const newPendingCount = newInteractions.filter(i => i.status === 'pending').length;
        
        // Show notification if there are new pending requests
        if (newPendingCount > oldPendingCount) {
          const newRequests = newPendingCount - oldPendingCount;
          toast.info(`${newRequests} new request${newRequests > 1 ? 's' : ''} received!`, {
            description: 'Check the Interactions tab for details',
            duration: 5000
          });
        }
        
        // Update state
        setInteractions(newInteractions);
        setUnreadMessages(unreadRes.data.unread_count || 0);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };
    
    // Poll every 15 seconds
    const pollInterval = setInterval(pollForUpdates, 15000);
    
    return () => clearInterval(pollInterval);
  }, [user, interactions.length]);

  const fetchRepData = async () => {
    try {
      // For new unified auth, we can get booth info from user object
      if (user && user.role === 'rep' && user.booth_id) {
        // Fetch booth and seller info
        const boothRes = await axios.get(`${API}/booths`);
        const userBooth = boothRes.data.find(b => b.id === user.booth_id);
        setBooth(userBooth);
        
        // Set rep info from user
        setRep({
          id: user.id,
          email: user.email,
          name: user.name,
          booth_id: user.booth_id,
          seller_id: user.seller_id
        });
        
        // Get seller info if available
        if (user.seller_id) {
          try {
            // We don't have a direct endpoint for this, so we'll skip or use booth info
            setSeller(userBooth?.seller || null);
          } catch (e) {
            console.log('Could not fetch seller info');
          }
        }
      } else {
        // Legacy system - use /reps/me endpoint
        const response = await axios.get(`${API}/reps/me`);
        setRep(response.data.rep);
        setBooth(response.data.booth);
        setSeller(response.data.seller);
      }
      
      // Fetch products and interactions
      // For new system, use booth_id from user; for legacy, use rep-specific endpoints
      if (user && user.role === 'rep' && user.booth_id) {
        const [productsRes, interactionsRes, unreadRes] = await Promise.all([
          axios.get(`${API}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/interactions/booth/${user.booth_id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { unread_count: 0 } }))
        ]);
        
        // Filter products for this booth
        setProducts(productsRes.data.filter(p => p.booth_id === user.booth_id));
        setInteractions(interactionsRes.data);
        setUnreadMessages(unreadRes.data.unread_count || 0);
      } else {
        // Legacy rep endpoints
        const [productsRes, interactionsRes, unreadRes] = await Promise.all([
          axios.get(`${API}/reps/products`),
          axios.get(`${API}/reps/interactions`),
          axios.get(`${API}/messages/unread-count/rep`).catch(() => ({ data: { unread_count: 0 } }))
        ]);
        
        setProducts(productsRes.data);
        setInteractions(interactionsRes.data);
        setUnreadMessages(unreadRes.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch rep data:', error);
      if (error.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('rep_token');
        if (logout) logout();
        navigate('/auth');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear both legacy and new auth
    localStorage.removeItem('rep_token');
    delete axios.defaults.headers.common['Authorization'];
    if (logout) {
      logout();
    }
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_queue: 'bg-blue-100 text-blue-700',
      on_call: 'bg-green-100 text-green-700',
      completed: 'bg-slate-100 text-slate-600',
      email_sent: 'bg-purple-100 text-purple-700',
      email_responded: 'bg-green-100 text-green-700',
    };
    const statusLabels = {
      pending: t('rep_dashboard.status_pending'),
      in_queue: t('rep_dashboard.status_in_queue'),
      on_call: t('rep_dashboard.status_on_call'),
      completed: t('rep_dashboard.status_completed'),
      email_sent: t('rep_dashboard.status_email_sent'),
      email_responded: t('rep_dashboard.status_email_responded'),
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-600'}>{statusLabels[status] || status?.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const startVideoCall = async (interaction) => {
    try {
      toast.loading('Creating video room...');
      
      // Get token - try main auth first, then legacy
      const token = localStorage.getItem('token') || localStorage.getItem('rep_token');
      if (!token) {
        toast.dismiss();
        toast.error('Session expired. Please log in again.');
        navigate('/auth');
        return;
      }
      
      console.log('[StartCall] Attempting to create room for interaction:', interaction.id);
      console.log('[StartCall] Using API URL:', `${API}/video/create-room?interaction_id=${interaction.id}`);
      
      const response = await axios.post(
        `${API}/video/create-room?interaction_id=${interaction.id}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('[StartCall] Response:', response.data);
      toast.dismiss();
      
      if (!response.data.room_url || !response.data.token) {
        console.error('[StartCall] Invalid response - missing room_url or token:', response.data);
        toast.error('Invalid response from server');
        return;
      }
      
      setActiveVideoRoom({
        roomUrl: response.data.room_url,
        token: response.data.token,
        interactionId: response.data.interaction_id,
        buyerName: interaction.buyer_name,
        buyerCompany: interaction.buyer?.company || interaction.buyer_company,
        boothCompany: booth?.company_name
      });
      setShowVideoCall(true);
      toast.success('Video room created! Waiting for attendee to join...');
      // Note: Don't call fetchRepData() here as it causes re-render that disrupts VideoCall
    } catch (error) {
      console.error('[StartCall] Error:', error);
      console.error('[StartCall] Response:', error.response);
      toast.dismiss();
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('rep_token');
        window.location.href = '/rep-login';
        return;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create video room';
      console.error('[StartCall] Error message:', errorMsg);
      toast.error(errorMsg);
    }
  };

  const joinExistingCall = async (interaction) => {
    try {
      toast.loading('Joining call...');
      const token = localStorage.getItem('rep_token');
      const response = await axios.get(`${API}/video/join/${interaction.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.dismiss();
      
      setActiveVideoRoom({
        roomUrl: response.data.room_url,
        token: response.data.token,
        interactionId: response.data.interaction_id,
        buyerName: interaction.buyer_name,
        buyerCompany: interaction.buyer?.company || interaction.buyer_company,
        boothCompany: booth?.company_name
      });
      setShowVideoCall(true);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to join call');
    }
  };

  const endVideoCall = async () => {
    if (!activeVideoRoom?.interactionId) return;
    
    try {
      const token = localStorage.getItem('rep_token');
      await axios.post(`${API}/video/end/${activeVideoRoom.interactionId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setShowVideoCall(false);
      setActiveVideoRoom(null);
      toast.success('Call ended');
      fetchRepData();
    } catch (error) {
      console.error('Failed to end call:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!rep || !booth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">{t('rep_dashboard.no_booth_assigned')}</h3>
            <p className="text-slate-500 mt-2">{t('rep_dashboard.no_booth_desc')}</p>
            <Button onClick={handleLogout} variant="outline" className="mt-4">
              <LogOut className="h-4 w-4 mr-2" /> {t('rep_dashboard.logout')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{t('rep_dashboard.title')}</h1>
                <p className="text-slate-500">{t('rep_dashboard.welcome')}, {rep.name}</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> {t('rep_dashboard.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Booth Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {booth.company_name}
                </CardTitle>
                <CardDescription>
                  {booth.slogan ? (
                    <AutoTranslatedText text={booth.slogan} />
                  ) : (
                    t('rep_dashboard.no_slogan')
                  )}
                </CardDescription>
              </div>
              <Badge className={
                booth.tier === 'elite' ? 'bg-purple-100 text-purple-700' :
                booth.tier === 'premium' ? 'bg-orange-100 text-orange-700' :
                'bg-slate-100 text-slate-600'
              }>
                {booth.tier?.toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">{t('rep_dashboard.products')}</p>
                <p className="font-semibold text-lg">{products.length}</p>
              </div>
              <div>
                <p className="text-slate-500">{t('rep_dashboard.pending_requests')}</p>
                <p className="font-semibold text-lg">{interactions.filter(i => i.status === 'in_queue').length}</p>
              </div>
              <div>
                <p className="text-slate-500">{t('rep_dashboard.active_calls')}</p>
                <p className="font-semibold text-lg">{interactions.filter(i => i.status === 'on_call').length}</p>
              </div>
              <div>
                <p className="text-slate-500">{t('rep_dashboard.total_interactions')}</p>
                <p className="font-semibold text-lg">{interactions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="interactions">
          <TabsList className="mb-4">
            <TabsTrigger value="interactions">
              <MessageSquare className="h-4 w-4 mr-2" /> {t('rep_dashboard.buyer_requests')}
            </TabsTrigger>
            <TabsTrigger value="messages" className="relative">
              <Send className="h-4 w-4 mr-2" /> {t('rep_dashboard.messages')}
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" /> {t('rep_dashboard.products')}
            </TabsTrigger>
          </TabsList>

          {/* Interactions Tab */}
          <TabsContent value="interactions">
            <Card>
              <CardHeader>
                <CardTitle>{t('rep_dashboard.buyer_interactions')}</CardTitle>
                <CardDescription>{t('rep_dashboard.buyer_interactions_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {interactions.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t('rep_dashboard.no_interactions')}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('rep_dashboard.buyer')}</TableHead>
                        <TableHead>{t('rep_dashboard.company')}</TableHead>
                        <TableHead>{t('rep_dashboard.type')}</TableHead>
                        <TableHead>{t('rep_dashboard.message')}</TableHead>
                        <TableHead>{t('rep_dashboard.status')}</TableHead>
                        <TableHead>{t('rep_dashboard.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {interactions.map((interaction) => (
                        <TableRow key={interaction.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{interaction.buyer_name}</p>
                              <p className="text-xs text-slate-500">{interaction.buyer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{interaction.buyer_company || '-'}</TableCell>
                          <TableCell>
                            {interaction.interaction_type === 'call_request' ? (
                              <span className="flex items-center text-blue-600">
                                <Video className="h-4 w-4 mr-1" /> {t('rep_dashboard.video_call')}
                              </span>
                            ) : (
                              <span className="flex items-center text-purple-600">
                                <Mail className="h-4 w-4 mr-1" /> {t('rep_dashboard.email')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{interaction.message || '-'}</TableCell>
                          <TableCell>{getStatusBadge(interaction.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {interaction.status === 'in_queue' && interaction.interaction_type === 'call_request' && (
                                <Button size="sm" onClick={() => startVideoCall(interaction)}>
                                  <Video className="h-4 w-4 mr-1" /> {t('rep_dashboard.start_call')}
                                </Button>
                              )}
                              {interaction.status === 'on_call' && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => joinExistingCall(interaction)}>
                                    <Phone className="h-4 w-4 mr-1" /> {t('rep_dashboard.join')}
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={endVideoCall}>
                                    {t('rep_dashboard.end')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  {t('rep_dashboard.messages')}
                </CardTitle>
                <CardDescription>{t('rep_dashboard.messages_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <MessagingPanel userType="seller" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>{t('rep_dashboard.booth_products')}</CardTitle>
                <CardDescription>{t('rep_dashboard.booth_products_desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t('rep_dashboard.no_products')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {products.map((product) => (
                      <div key={product.id} className="border border-slate-200 rounded-lg p-4">
                        {product.image_url && (
                          <img src={product.image_url} alt={product.name} className="w-full h-32 object-cover rounded mb-3" />
                        )}
                        <h4 className="font-semibold">{product.name}</h4>
                        <p className="text-sm text-slate-600 line-clamp-2">{product.description}</p>
                        {product.price && (
                          <p className="text-primary font-semibold mt-2">{formatPrice(product.price)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Video Call Overlay - NOT using Dialog to avoid React DOM conflicts with Daily.co */}
      {showVideoCall && activeVideoRoom && (
        <VideoCall
          roomUrl={activeVideoRoom.roomUrl}
          token={activeVideoRoom.token}
          userName={rep?.name || 'Representative'}
          onCallEnd={endVideoCall}
          onError={(error) => {
            console.error('Video call error:', error);
            toast.error('Video call error');
          }}
        />
      )}
    </div>
  );
};

export default RepDashboard;
